<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import AdsConversationTrackingCard from './AdsConversationTrackingCard.vue';
import { buildPaidTrackingSummary } from '../trackingSummary';
import { useRastroEnabled } from '../useRastro';

const mountTarget = ref(null);
let scheduledFrame;
const { uiSettings } = useUISettings();
const isRastroEnabled = useRastroEnabled();

const selectedConversation = useMapGetter('getSelectedChat');
const conversationMetadata = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);

const additionalAttributes = computed(() => {
  const conversation = selectedConversation.value || {};
  const metadata = conversation.id
    ? conversationMetadata.value(conversation.id)
    : {};

  return (
    metadata?.additional_attributes || conversation.additional_attributes || {}
  );
});

const trackingSummary = computed(() =>
  buildPaidTrackingSummary(additionalAttributes.value)
);

const ensureMountTarget = () => {
  if (!isRastroEnabled.value) {
    mountTarget.value?.remove();
    mountTarget.value = null;
    return;
  }
  if (mountTarget.value?.isConnected) return;

  const sidebarContent = document.querySelector('.list-group');
  if (!sidebarContent) {
    mountTarget.value = null;
    return;
  }

  let target = sidebarContent.querySelector('[data-ads-tracking-target]');
  if (!target) {
    target = document.createElement('div');
    target.dataset.adsTrackingTarget = '';
    sidebarContent.prepend(target);
  }
  mountTarget.value = target;
};

const scheduleMountTarget = () => {
  if (scheduledFrame) return;

  scheduledFrame = window.requestAnimationFrame(() => {
    scheduledFrame = null;
    ensureMountTarget();
  });
};

const refreshMountTarget = async () => {
  await nextTick();
  scheduleMountTarget();
};

watch(
  [
    isRastroEnabled,
    () => selectedConversation.value?.id,
    () => uiSettings.value?.is_contact_sidebar_open,
  ],
  refreshMountTarget
);

onMounted(refreshMountTarget);

onBeforeUnmount(() => {
  window.cancelAnimationFrame(scheduledFrame);
  mountTarget.value?.remove();
});
</script>

<template>
  <Teleport v-if="isRastroEnabled && mountTarget && trackingSummary" :to="mountTarget">
    <AdsConversationTrackingCard :summary="trackingSummary" />
  </Teleport>
</template>
